/**
 * 자리별 목록 취향 기억 (2026-08-22 사용자 지시).
 *
 * 첫 값은 관리자 설정(`listStyle`)이 주지만, 회원이 그 자리에서 뷰(카드/리스트)와 정렬을
 * 바꾸면 **다음에 왔을 때 그대로** 보여야 한다.
 *
 * 기억은 **자리(모듈 인스턴스)마다 갈린다** — 강좌 목록에서 리스트형으로 바꿔도 교재 목록은
 * 제 기억을 따로 갖는다. 그래서 목록 모듈이 `storageKey` 를 받는다.
 *
 * **쿠키에 담는다**(2026-08-23 사용자 지적으로 localStorage 에서 옮김 —
 * "기본 카드형이었다가 깜박이며 리스트형으로 바뀐다").
 * localStorage 는 브라우저에만 있어서 **서버가 첫 화면을 그릴 때 알 수 없다**. 그래서
 * 설정 기본값(카드)으로 그린 뒤 마운트 후 리스트로 바꿔 그리게 되고, 그 사이가 깜박임이었다.
 * 쿠키는 요청과 함께 오므로 **SSR 이 처음부터 옳게 그린다** — 현행도 뷰를 쿠키
 * (`goods_listview`)로 기억했다. 자리가 여럿이라 **한 쿠키에 자리→취향 지도**로 담는다.
 */

/** 쿠키 이름 — 자리마다 하나씩 두면 요청마다 다 실려 나간다. 한 칸에 지도로 담는다 */
export const PREFS_COOKIE = 'listprefs';
/** 1년 — 취향이라 오래 기억해도 잃을 것이 없다 */
const MAX_AGE = 60 * 60 * 24 * 365;

/** 정렬 탭 — 인기순이 있다(서버에서는 featuredFirst 조건이다) */
export type SortKey = 'popular' | 'latest' | 'name' | 'price';

export interface ListPrefs {
  view: 'card' | 'list';
  sort: SortKey;
  dir: 'asc' | 'desc';
}

const SORTS: SortKey[] = ['popular', 'latest', 'name', 'price'];

/** 한 자리의 값 — 모양이 어긋나면 `null`(설정 기본값을 그대로 쓴다) */
function one(v: unknown): ListPrefs | null {
  if (!v || typeof v !== 'object') return null;
  const p = v as Partial<ListPrefs>;
  if (p.view !== 'card' && p.view !== 'list') return null;
  if (!SORTS.includes(p.sort as SortKey)) return null;
  return { view: p.view, sort: p.sort as SortKey, dir: p.dir === 'asc' ? 'asc' : 'desc' };
}

/**
 * 쿠키 값 → 한 자리의 취향. **서버·클라이언트가 함께 쓴다**(같은 규칙으로 읽어야
 * SSR 과 hydration 이 어긋나지 않는다).
 */
export function prefsFromCookie(raw: string | undefined, storageKey: string): ListPrefs | null {
  if (!raw) return null;
  try {
    const map = JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>;
    return one(map?.[storageKey]);
  } catch {
    return null; // 손상된 쿠키 — 기본값으로 간다
  }
}

/** 브라우저에서 읽기 */
export function readPrefs(storageKey: string): ListPrefs | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie.split('; ').find((c) => c.startsWith(`${PREFS_COOKIE}=`))?.slice(PREFS_COOKIE.length + 1);
  return prefsFromCookie(raw, storageKey);
}

/** 브라우저에서 쓰기 — 지도를 읽어 그 자리만 갈아 끼운다 */
export function writePrefs(storageKey: string, prefs: ListPrefs): void {
  if (typeof document === 'undefined') return;
  try {
    const raw = document.cookie.split('; ').find((c) => c.startsWith(`${PREFS_COOKIE}=`))?.slice(PREFS_COOKIE.length + 1);
    const map = raw ? (JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>) : {};
    map[storageKey] = prefs;
    const value = encodeURIComponent(JSON.stringify(map));
    document.cookie = `${PREFS_COOKIE}=${value}; path=/; max-age=${MAX_AGE}; samesite=lax`;
  } catch {
    // 쿠키를 막아 둔 브라우저 — 화면은 그대로 동작한다(기억만 안 될 뿐)
  }
}

/**
 * 설정값 → 첫 취향. **관리자의 '추천 먼저' 체크가 곧 초기 탭이 인기순인가**다(§④-d).
 * 인기순은 매긴 차례가 곧 순서라 방향을 뒤집지 않는다.
 */
export function prefsFromStyle(style: {
  view: 'card' | 'list';
  order: 'latest' | 'name' | 'price';
  dir: 'asc' | 'desc';
  featuredFirst: boolean;
}): ListPrefs {
  return {
    view: style.view,
    sort: style.featuredFirst ? 'popular' : style.order,
    dir: style.dir,
  };
}

/**
 * 설정값 → 첫 취향 (**구성한 자리**용).
 *
 * 첫 화면 상품 섹션·프로모션 묶음은 관리자가 **직접 배열한 차례**가 있고, 그것이 이 자리의
 * '인기순' 이다(2026-08-23 사용자 확정). 그래서 위(`prefsFromStyle`)와 딱 한 가지가 다르다 —
 * 마켓의 '추천 먼저' 설정을 보지 않고 **늘 인기순으로 시작한다**. 애써 배열해 둔 순서를
 * 첫 화면부터 다른 기준으로 흩어 놓으면 구성이 뜻을 잃는다.
 * 뷰·방향은 그대로 설정을 따른다(나머지 옵션은 기본설정을 따른다는 원칙).
 */
export function prefsFromComposedOrder(style: {
  view: 'card' | 'list';
  dir: 'asc' | 'desc';
}): ListPrefs {
  return { view: style.view, sort: 'popular', dir: style.dir };
}

/**
 * 취향 → 서버 쿼리 조각.
 * 인기순은 항목이 아니라 **얹는 조건**(`featuredFirst`)이므로, 2차 정렬로 설정의 초기 정렬을
 * 함께 넘긴다 — 노출 순서를 매긴 상품은 앞쪽 몇 개뿐이라 나머지를 정렬할 기준이 늘 필요하다.
 */
export function prefsToQuery(
  prefs: ListPrefs,
  style: { order: 'latest' | 'name' | 'price'; dir: 'asc' | 'desc' },
): Record<string, string> {
  if (prefs.sort === 'popular') {
    return { featuredFirst: 'true', order: style.order, dir: style.dir };
  }
  return { order: prefs.sort, dir: prefs.dir };
}

/* ── 마켓 기본 보기 — 목록 뼈대(스켈레톤)가 첫 화면부터 옳은 모양을 그리기 위한 사본 ──
   (2026-08-28 사용자 지시 "api호출하지 말고 … 처음 들어올 때 정보 받는 곳에서 미리 저장해둔걸로")

   `loading.tsx` 는 페이지가 준비되기 **전에** 그려지는 자리라, 여기서 관리자 설정을 읽으려고
   API 를 부르면 뼈대가 그만큼 늦게 뜬다 — 뼈대의 존재 이유를 깎는 일이다. 셸이 어차피 받는
   `listStyle` 을 브라우저가 쿠키에 적어 두면, 다음부터는 **읽기만** 하면 된다.

   취향(PREFS_COOKIE)과 갈라 둔다 — 저쪽은 회원이 바꾼 값이고 이쪽은 관리자 설정의 사본이다.
   **설정이 바뀌면 한 번은 낡은 값으로 뼈대를 그린다**(다음 방문에 덮인다). 뼈대일 뿐이라
   그 정도는 감당하고, 대신 API 호출을 늘리지 않는다. */

/** 마켓 기본 목록 꼴 쿠키 — `{ lecture: {v,t,c,s}, book: … }` */
export const VIEW_COOKIE = 'listview';

export type ListKind = 'lecture' | 'book';

/**
 * 뼈대가 그릴 **목록 꼴** — 보기 + 제목 아래 줄들이 뜨는지.
 *
 * 보기 하나만 적어 두었더니 뼈대가 실물보다 **네 줄 짧았다**(2026-08-31 사용자 제보).
 * 실물의 제목 아래는 종류 탭 · 분류 칩 · 검색 바 · 정렬 줄이고, 뼈대는 44px 짜리 한 줄만
 * 그려 실물이 들어오는 순간 목록이 200px 남짓 아래로 밀렸다. 어느 줄이 뜨는지는 마켓 설정
 * (`typeTab`·`categoryTab`·`sortControls`)이 정하므로, 보기와 **같은 방식으로** 적어 둔다.
 */
export interface ListChrome {
  view: 'card' | 'list';
  /** 종류 탭(전체·단과·패키지·프리패스) 줄 */
  typeTab: boolean;
  /** 분류 칩 줄 — 뼈대는 **맨 위 한 단계만** 그린다(고르기 전에는 실물도 한 줄이다) */
  catTab: boolean;
  /** 정렬 탭·보기 토글 줄 */
  sortRow: boolean;
}

/** 쿠키에 적히는 꼴 — 짧게 쓴다(쿠키는 요청마다 실려 간다) */
type Packed = { v: 'card' | 'list'; t: 1 | 0; c: 1 | 0; s: 1 | 0 };

/**
 * 관리자 설정 → 뼈대가 쓸 꼴. **판정을 한 곳에 둔다** — 셸과 뼈대가 따로 셈하면 갈린다.
 * 분류 줄은 설정이 켜져 있어도 등록된 분류가 없으면 실물에 안 뜬다(그 마켓에서는 뼈대가
 * 한 줄 더 그린다). 셸은 분류를 받지 않으므로 여기까지가 API 를 늘리지 않는 한계다.
 */
export function listChrome(s: {
  view: 'card' | 'list'; typeTab: boolean; categoryTab: string; sortControls: boolean; viewToggle: boolean;
}): ListChrome {
  return {
    view: s.view,
    typeTab: s.typeTab,
    catTab: s.categoryTab !== 'none',
    sortRow: s.sortControls || s.viewToggle,
  };
}

/**
 * 쿠키 값 → 그 갈래의 목록 꼴. 없거나 손상되면 `null`(부르는 쪽이 안전값을 쓴다).
 * **옛 꼴(`'card'` 글자 하나)도 읽는다** — 쿠키는 최대 30일 살아 있어 갈아타는 동안 섞인다.
 * 그때는 줄이 다 있는 것으로 본다(가이드 기본값이자 대부분 마켓의 설정이다).
 */
export function chromeFromCookie(raw: string | undefined, kind: ListKind): ListChrome | null {
  if (!raw) return null;
  try {
    const map = JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>;
    const v = map?.[kind];
    if (v === 'card' || v === 'list') return { view: v, typeTab: true, catTab: true, sortRow: true };
    if (!v || typeof v !== 'object') return null;
    const p = v as Partial<Packed>;
    if (p.v !== 'card' && p.v !== 'list') return null;
    return { view: p.v, typeTab: p.t !== 0, catTab: p.c !== 0, sortRow: p.s !== 0 };
  } catch {
    return null;
  }
}

/** 브라우저에서 쓰기 — 값이 그대로면 손대지 않는다(쿠키를 매 렌더 다시 쓸 이유가 없다) */
export function writeListChrome(next: Record<ListKind, ListChrome>): void {
  if (typeof document === 'undefined') return;
  try {
    const pack = (c: ListChrome): Packed => ({
      v: c.view, t: c.typeTab ? 1 : 0, c: c.catTab ? 1 : 0, s: c.sortRow ? 1 : 0,
    });
    const raw = document.cookie.split('; ').find((c) => c.startsWith(`${VIEW_COOKIE}=`))?.slice(VIEW_COOKIE.length + 1);
    const value = encodeURIComponent(JSON.stringify({ lecture: pack(next.lecture), book: pack(next.book) }));
    if (raw === value) return;
    document.cookie = `${VIEW_COOKIE}=${value}; path=/; max-age=${MAX_AGE}; samesite=lax`;
  } catch {
    // 쿠키를 막아 둔 브라우저 — 뼈대가 기본 모양으로 나올 뿐 화면은 그대로 동작한다
  }
}
